import { Tooltip } from '@mui/material';
import TooltipContent from './TooltipContent';
import ChipContent from './ChipContent';
import { useRef, useEffect } from 'react';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const EventChip = ({ eventInfo, onLongPressChange, setCopyId }) => {
  const setBackgroundOpacity = (rgba, opacity) => {
    return rgba.replace(/[\d\.]+\)$/g, `${opacity})`);
  };

  // long-press support
  const pressTimerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const longPressActiveRef = useRef(false); // prevent drag while true
  const LONG_PRESS_MS = 600;
  const MOVE_THRESHOLD_PX = 10;

  // short-lived click suppression after long-press release
  const suppressClickRef = useRef(false);
  const suppressResetTimerRef = useRef(null);

  useEffect(() => {
    // Global failsafes to ensure we always exit long-press/copy mode
    const cancelOnGlobalEnd = () => cancelPress();
    const endEvents = ['touchend', 'touchcancel', 'mouseup', 'pointerup', 'pointercancel'];
    endEvents.forEach((t) => window.addEventListener(t, cancelOnGlobalEnd, { passive: true }));
    const onVisibility = () => cancelPress();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onVisibility);

    return () => {
      endEvents.forEach((t) => window.removeEventListener(t, cancelOnGlobalEnd));
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onVisibility);
      // cleanup just in case
      clearTimeout(pressTimerRef.current);
      clearTimeout(suppressResetTimerRef.current);
      // ensure we exit copy mode if component unmounts mid-press
      if (longPressActiveRef.current) {
        longPressActiveRef.current = false;
        onLongPressChange?.(false);
        setCopyId?.(null);
      }
    };
  }, []);

  const startPress = (clientX, clientY) => {
    startPosRef.current = { x: clientX, y: clientY };
    clearTimeout(pressTimerRef.current);
    longPressActiveRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      longPressActiveRef.current = true;

      onLongPressChange?.(true);
      addNotification({
        content: 'Másolási mód aktiválva!',
        type: NotificationType.INFO,
      });
      setCopyId(eventInfo.event._def.extendedProps.eventData.simpleTaskId);
    }, LONG_PRESS_MS);
  };

  const cancelPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    const wasLongPress = longPressActiveRef.current;
    if (wasLongPress) {
      // suppress the immediate click that follows mouseup after long-press
      suppressClickRef.current = true;
      clearTimeout(suppressResetTimerRef.current);
      suppressResetTimerRef.current = setTimeout(() => {
        suppressClickRef.current = false;
      }, 300);
    }
    if (longPressActiveRef.current) {
      longPressActiveRef.current = false;
      onLongPressChange?.(false);
    }
  };

  const maybeCancelOnMove = (clientX, clientY) => {
    const dx = clientX - startPosRef.current.x;
    const dy = clientY - startPosRef.current.y;
    if (Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) {
      cancelPress();
    }
  };

  const onMouseDown = (e) => startPress(e.clientX, e.clientY);
  const onMouseUp = () => cancelPress();
  const onMouseMove = (e) => {
    if (longPressActiveRef.current) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    maybeCancelOnMove(e.clientX, e.clientY);
  };

  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (t) startPress(t.clientX, t.clientY);
  };
  const onTouchEnd = () => cancelPress();
  const onTouchCancel = () => cancelPress();
  const onTouchMove = (e) => {
    if (longPressActiveRef.current) {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      return;
    }
    const t = e.touches?.[0];
    if (t) maybeCancelOnMove(t.clientX, t.clientY);
  };

  const onContextMenu = (e) => {
    if (longPressActiveRef.current || pressTimerRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onDragStart = (e) => {
    if (longPressActiveRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleMouseEnter = (e) => {
    const eventElement = e.currentTarget.closest('.fc-h-event');
    eventElement.style.border = `2px solid ${eventInfo.textColor}`;
    eventElement.style.backgroundColor = setBackgroundOpacity(eventInfo.backgroundColor, 0.9);
  };

  const handleMouseLeave = (e) => {
    const eventElement = e.currentTarget.closest('.fc-h-event');
    eventElement.style.backgroundColor = setBackgroundOpacity(eventInfo.backgroundColor, 0.6);
    eventElement.style.border = `2px solid transparent`;
  };

  const onClick = (e) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
      suppressClickRef.current = false;
      return;
    }
  };

  return (
    <Tooltip
      arrow
      placement="top"
      title={<TooltipContent eventInfo={eventInfo} />}
      slotProps={{
        tooltip: {
          sx: {
            color: '#514E6A',
            backgroundColor: '#ffff',
            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
            minWidth: 300,
            maxWidth: '100%',
          },
        },
        arrow: { sx: { color: '#ffff' } },
      }}
    >
      <div
        className="font-bold truncate my-auto px-1"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onTouchMove={onTouchMove}
        onContextMenu={onContextMenu}
        onDragStart={onDragStart}
        onClick={onClick}
      >
        <ChipContent eventInfo={eventInfo} />
      </div>
    </Tooltip>
  );
};

export default EventChip;
